import React, { useState, useEffect } from 'react';
import { matchesAPI, liveStreamsAPI } from '../services/api';
import { FaYoutube, FaTv, FaExpand, FaThLarge } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';

const getEmbedUrl = (url, autoplay = false) => {
  if (!url) return null;

  // Si c'est juste un ID YouTube (11 caractères)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return `https://www.youtube.com/embed/${url}${autoplay ? '?autoplay=1' : ''}`;
  }

  // YouTube URL
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('v=')) {
      videoId = url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    } else if (url.includes('live/')) {
      videoId = url.split('live/')[1].split('?')[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}${autoplay ? '?autoplay=1' : ''}`;
    }
  }

  // Facebook URL
  if (url.includes('facebook.com') || url.includes('fb.watch')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0&width=560${autoplay ? '&autoplay=1' : ''}`;
  }

  return url;
};

const getThumbnailUrl = (url) => {
  if (!url) return null;

  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return `https://img.youtube.com/vi/${url}/mqdefault.jpg`;
  }

  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('v=')) {
      videoId = url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    } else if (url.includes('live/')) {
      videoId = url.split('live/')[1].split('?')[0];
    }
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
  }

  return null;
};

const LivePage = () => {
  const [liveItems, setLiveItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeItemId, setActiveItemId] = useState(null);
  const [layoutMode, setLayoutMode] = useState('grid'); // 'focus' or 'grid'

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000); // Actualisation automatique
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [matchesRes, streamsRes] = await Promise.all([
        matchesAPI.getAll().catch(() => ({ data: [] })),
        liveStreamsAPI.getAll().catch(() => ({ data: [] }))
      ]);

      const matchesData = Array.isArray(matchesRes.data) ? matchesRes.data : [];

      // Récupérer les matchs ayant status === 'live'
      const liveMatches = matchesData
        .filter(m => m.status === 'live' || m.youtube_url)
        .map(m => ({
          id: `match_${m.id}`,
          originalId: m.id,
          type: 'match',
          title: m.home_team_name && m.away_team_name ? `بث مباشر: ${m.home_team_name} vs ${m.away_team_name}` : 'بث مباشر مباراة',
          title_ar: m.home_team_name && m.away_team_name ? `بث مباشر: ${m.home_team_name} vs ${m.away_team_name}` : 'بث مباشر مباراة',
          youtube_url: m.youtube_url,
          home_team_name: m.home_team_name,
          away_team_name: m.away_team_name,
          home_score: m.home_score,
          away_score: m.away_score
        }));

      // Récupérer les flux en direct autonomes
      const customStreams = (Array.isArray(streamsRes.data) ? streamsRes.data : []).map(s => ({
        id: `stream_${s.id}`,
        originalId: s.id,
        type: 'stream',
        title: s.title_ar || s.title || 'بث مباشر',
        title_ar: s.title_ar || s.title || 'بث مباشر',
        youtube_url: s.youtube_url,
        home_team_name: s.home_team_name,
        away_team_name: s.away_team_name,
        home_score: s.home_score,
        away_score: s.away_score
      }));

      // Fusionner les flux sans doublons
      const combined = [...customStreams, ...liveMatches.filter(lm => !customStreams.some(cs => cs.youtube_url === lm.youtube_url))];
      setLiveItems(combined);

      if (combined.length > 0 && !activeItemId) {
        setActiveItemId(combined[0].id);
      }
    } catch (error) {
      console.error('Erreur de chargement des flux:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const activeItem = liveItems.find(item => item.id === activeItemId) || liveItems[0];

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <section style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.liveIndicator}>
            <span style={styles.liveDot}></span>
            <span style={styles.liveText}>فضاء البث المباشر • DIRECT MULTI-ÉCRANS</span>
          </div>

          <h1 style={styles.title}>Ligue Amateur TV</h1>

          <div style={styles.headerActions}>
            <div style={styles.layoutToggles}>
              <button
                onClick={() => setLayoutMode('grid')}
                style={{ ...styles.layoutBtn, ...(layoutMode === 'grid' ? styles.layoutBtnActive : {}) }}
              >
                <FaThLarge /> Mosaïque
              </button>
              <button
                onClick={() => setLayoutMode('focus')}
                style={{ ...styles.layoutBtn, ...(layoutMode === 'focus' ? styles.layoutBtnActive : {}) }}
              >
                <FaExpand /> Focus
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={styles.mainContent}>
        {liveItems.length > 0 ? (
          layoutMode === 'focus' ? (
            <div style={styles.focusLayout}>
              {/* Main Player */}
              <div style={styles.mainPlayer}>
                {/* Title Banner on Main Player */}
                <div style={styles.playerTitleBar}>
                  <div style={styles.playerTitleBadge}>🔴 مباشر</div>
                  <h2 style={styles.playerTitleText}>{activeItem?.title || activeItem?.title_ar}</h2>
                </div>

                {activeItem?.youtube_url ? (
                  <div style={styles.videoWrapper}>
                    <iframe
                      width="100%"
                      height="100%"
                      src={getEmbedUrl(activeItem.youtube_url, true)}
                      title={activeItem.title || 'Live Stream'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <div style={styles.noVideo}>
                    <FaYoutube size={80} color="#ff0000" />
                    <p>Pas de flux vidéo pour ce live</p>
                  </div>
                )}

                {activeItem?.home_team_name && activeItem?.away_team_name && (
                  <div style={styles.matchDetailOverlay}>
                    <div style={styles.overlayTeams}>
                      <span>{activeItem.home_team_name}</span>
                      <span style={styles.overlayScore}>
                        {activeItem.home_score ?? 0} - {activeItem.away_score ?? 0}
                      </span>
                      <span>{activeItem.away_team_name}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar Thumbnails */}
              <div style={styles.sidebar}>
                <h3 style={styles.sidebarTitle}>قائمة البث المباشر (LIVES)</h3>
                {liveItems.map(item => (
                  <div
                    key={item.id}
                    style={{
                      ...styles.thumbCard,
                      ...(activeItemId === item.id ? styles.thumbCardActive : {})
                    }}
                    onClick={() => setActiveItemId(item.id)}
                  >
                    <div style={styles.thumbPreview}>
                      {item.youtube_url && getThumbnailUrl(item.youtube_url) ? (
                        <img
                          src={getThumbnailUrl(item.youtube_url)}
                          alt="Preview"
                          style={styles.thumbImg}
                        />
                      ) : (
                        <div style={{ ...styles.thumbImg, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222' }}>
                          <FaYoutube size={30} color="#ff0000" />
                        </div>
                      )}
                      <div style={styles.thumbLiveBadge}>LIVE 🔴</div>
                    </div>
                    <div style={styles.thumbInfo}>
                      <div style={styles.thumbTeams}>{item.title || item.title_ar}</div>
                      {item.home_team_name && item.away_team_name && (
                        <div style={styles.thumbScore}>
                          {item.home_team_name} {item.home_score ?? 0} - {item.away_score ?? 0} {item.away_team_name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Mosaic Grid Layout */
            <div style={styles.mosaicGrid}>
              {liveItems.map(item => (
                <div key={item.id} style={styles.gridCard}>
                  {/* Title Bar for Grid Card */}
                  <div style={styles.cardHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={styles.liveBadgeSmall}>🔴 مباشر</span>
                      <h3 style={styles.cardTitleText}>{item.title || item.title_ar}</h3>
                    </div>
                  </div>

                  <div style={styles.gridVideoWrapper}>
                    {item.youtube_url ? (
                      <iframe
                        width="100%"
                        height="100%"
                        src={getEmbedUrl(item.youtube_url)}
                        title={item.title || item.id}
                        frameBorder="0"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div style={styles.gridPlaceholder}>Pas de flux vidéo</div>
                    )}
                  </div>

                  <div style={styles.gridCardFooter}>
                    {item.home_team_name && item.away_team_name ? (
                      <div style={styles.gridCardInfo}>
                        <span style={styles.gridTeamName}>{item.home_team_name}</span>
                        <span style={styles.gridScore}>{item.home_score ?? 0} : {item.away_score ?? 0}</span>
                        <span style={styles.gridTeamName}>{item.away_team_name}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: '13px', color: '#aaa' }}>بث مباشر يوتيوب</div>
                    )}
                    <button
                      onClick={() => { setActiveItemId(item.id); setLayoutMode('focus'); }}
                      style={styles.gridExpandBtn}
                    >
                      FOCUS
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div style={styles.noLive}>
            <FaTv style={styles.noLiveIcon} />
            <h2>لا يوجد بث مباشر حالياً</h2>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0a',
    color: '#fff'
  },
  header: {
    padding: '30px 20px',
    background: 'linear-gradient(to bottom, #1a1a1a, #0a0a0a)',
    borderBottom: '1px solid #333'
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px'
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  liveDot: {
    width: '14px',
    height: '14px',
    background: '#ff0000',
    borderRadius: '50%',
    boxShadow: '0 0 12px #ff0000',
    animation: 'pulse 1.5s infinite'
  },
  liveText: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#ff0000'
  },
  title: {
    fontSize: '32px',
    fontWeight: '800',
    margin: 0,
    letterSpacing: '-1px'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flexWrap: 'wrap'
  },
  addLiveBtn: {
    background: 'linear-gradient(135deg, #ff0000, #c41e3a)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 15px rgba(255, 0, 0, 0.4)',
    transition: 'transform 0.2s, boxShadow 0.2s'
  },
  layoutToggles: {
    display: 'flex',
    background: '#222',
    padding: '5px',
    borderRadius: '10px'
  },
  layoutBtn: {
    background: 'transparent',
    border: 'none',
    color: '#888',
    padding: '8px 15px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s'
  },
  layoutBtnActive: {
    background: '#c41e3a',
    color: '#fff'
  },
  mainContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  focusLayout: {
    display: 'flex',
    gap: '25px',
    minHeight: '600px',
    flexWrap: 'wrap'
  },
  mainPlayer: {
    flex: 3,
    minWidth: '300px',
    background: '#000',
    borderRadius: '20px',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
  },
  playerTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 20px',
    background: '#151515',
    borderBottom: '1px solid #252525'
  },
  playerTitleBadge: {
    background: '#ff0000',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: '6px',
    fontWeight: 'bold',
    fontSize: '12px'
  },
  playerTitleText: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: 0,
    color: '#fff',
    flex: 1,
    textAlign: 'center'
  },
  deleteStreamBtn: {
    background: 'rgba(255, 0, 0, 0.15)',
    border: '1px solid #ff0000',
    color: '#ff0000',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  videoWrapper: {
    width: '100%',
    aspectRatio: '16 / 9'
  },
  noVideo: {
    width: '100%',
    aspectRatio: '16 / 9',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444'
  },
  matchDetailOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
    padding: '20px',
  },
  overlayTeams: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '30px',
    fontSize: '20px',
    fontWeight: 'bold'
  },
  overlayScore: {
    background: '#c41e3a',
    padding: '4px 16px',
    borderRadius: '5px',
    fontSize: '24px'
  },
  sidebar: {
    flex: 1,
    minWidth: '300px',
    background: '#151515',
    borderRadius: '20px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    maxHeight: '800px',
    overflowY: 'auto'
  },
  sidebarTitle: {
    fontSize: '15px',
    color: '#c41e3a',
    margin: '0 0 10px 0',
    fontWeight: 'bold'
  },
  thumbCard: {
    display: 'flex',
    gap: '15px',
    padding: '10px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    border: '1px solid transparent'
  },
  thumbCardActive: {
    background: '#222',
    borderColor: '#c41e3a'
  },
  thumbPreview: {
    width: '120px',
    height: '70px',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    background: '#000'
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  thumbLiveBadge: {
    position: 'absolute',
    top: '5px',
    left: '5px',
    background: '#ff0000',
    color: '#fff',
    fontSize: '8px',
    padding: '2px 5px',
    borderRadius: '3px',
    fontWeight: 'bold'
  },
  thumbInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '5px'
  },
  thumbTeams: {
    fontSize: '13px',
    fontWeight: '600'
  },
  thumbScore: {
    color: '#c41e3a',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  mosaicGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '30px'
  },
  gridCard: {
    background: '#151515',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    border: '1px solid #222'
  },
  cardHeader: {
    padding: '12px 15px',
    background: '#1d1d1d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #2a2a2a'
  },
  liveBadgeSmall: {
    background: '#ff0000',
    color: '#fff',
    fontSize: '11px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 'bold'
  },
  cardTitleText: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#fff',
    margin: 0
  },
  cardDeleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px'
  },
  gridVideoWrapper: {
    width: '100%',
    aspectRatio: '16 / 9',
    background: '#000'
  },
  gridPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#333'
  },
  gridCardFooter: {
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  gridCardInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  gridTeamName: {
    fontSize: '13px',
    fontWeight: 'bold'
  },
  gridScore: {
    background: '#222',
    padding: '4px 10px',
    borderRadius: '5px',
    color: '#ff0000',
    fontWeight: 'bold'
  },
  gridExpandBtn: {
    background: '#c41e3a',
    border: 'none',
    color: '#fff',
    padding: '8px 15px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  noLive: {
    textAlign: 'center',
    padding: '80px 20px',
    color: '#888'
  },
  noLiveIcon: {
    fontSize: '80px',
    marginBottom: '20px',
    opacity: 0.3
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modalContent: {
    background: '#181818',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '550px',
    border: '1px solid #333',
    boxShadow: '0 20px 50px rgba(0,0,0,0.8)',
    overflow: 'hidden'
  },
  modalHeader: {
    padding: '20px',
    background: '#222',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #333'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    display: 'flex',
    alignItems: 'center'
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#aaa',
    fontSize: '18px',
    cursor: 'pointer'
  },
  form: {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ddd'
  },
  input: {
    padding: '12px 15px',
    borderRadius: '8px',
    background: '#0d0d0d',
    border: '1px solid #333',
    color: '#fff',
    fontSize: '14px',
    outline: 'none'
  },
  select: {
    padding: '12px 15px',
    borderRadius: '8px',
    background: '#0d0d0d',
    border: '1px solid #333',
    color: '#fff',
    fontSize: '14px',
    outline: 'none'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '10px'
  },
  cancelBtn: {
    background: '#333',
    border: 'none',
    color: '#ccc',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  submitBtn: {
    background: 'linear-gradient(135deg, #ff0000, #c41e3a)',
    border: 'none',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold'
  }
};

export default LivePage;

